/* src/context/ChatContext.tsx */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactGA from "react-ga4";
import { useNeuralAudio } from "../lib/audio";

export interface Message {
  role: "user" | "assistant";
  content: string;
  /** How long the upstream answer took; rendered as meta under the bubble. */
  elapsedMs?: number;
  /** Suggested next questions for this answer. Empty array = unavailable. */
  followUps?: string[];
  followUpsLoading?: boolean;
}

interface ChatContextValue {
  messages: Message[];
  isOpen: boolean;
  /** True while waiting for the first token of an answer. */
  isTyping: boolean;
  /** True while an answer is streaming in. */
  isStreaming: boolean;
  /** True when the last request failed; the panel shows the retry block. */
  hasError: boolean;
  input: string;
  setInput: (value: string) => void;
  /** Opens the floating panel. If a prefill is provided, it is sent immediately. */
  openChat: (prefill?: string) => void;
  closeChat: () => void;
  /** Sends an explicit message to the bot (caller owns clearing its own input). */
  sendMessage: (text: string) => Promise<void>;
  /** Aborts the in-flight answer, keeping whatever tokens already arrived. */
  stopGenerating: () => void;
  /** Re-asks the last user question, replacing the answer that followed it. */
  regenerate: () => Promise<void>;
  /** Resends the question preserved in the composer after a failed request. */
  retryLast: () => Promise<void>;
  /** Aborts any in-flight request and resets the conversation to the welcome message. */
  clearChat: () => void;
}

const ChatContext = createContext<ChatContextValue | null>(null);

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t, i18n } = useTranslation();
  const { play } = useNeuralAudio();

  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [hasError, setHasError] = useState(false);
  const sessionId = useRef(`session-${Math.random().toString(36).substring(2, 9)}`).current;
  const abortRef = useRef<AbortController | null>(null);
  /** Mirrors isTyping || isStreaming so callbacks can gate without stale closures. */
  const busyRef = useRef(false);
  /** Bumped on clear/language switch so late follow-up responses are dropped. */
  const generationRef = useRef(0);
  const lastQuestionRef = useRef<string | null>(null);

  // Initialize / reset welcome message based on language
  useEffect(() => {
    abortRef.current?.abort();
    generationRef.current += 1;
    busyRef.current = false;
    lastQuestionRef.current = null;
    setIsTyping(false);
    setIsStreaming(false);
    setHasError(false);
    setMessages([{ role: "assistant", content: t("kevinBot.panel.welcome") }]);
  }, [i18n.language, t]);

  /** Patches the last assistant message, or the final message when `onlyIfLast`. */
  const patchLastAssistant = useCallback((patch: Partial<Message>, onlyIfLast = false) => {
    setMessages((prev) => {
      let index = -1;
      if (onlyIfLast) {
        index = prev.length - 1;
      } else {
        for (let i = prev.length - 1; i >= 0; i -= 1) {
          if (prev[i].role === "assistant") {
            index = i;
            break;
          }
        }
      }
      if (index < 0 || prev[index]?.role !== "assistant") return prev;
      const updated = [...prev];
      updated[index] = { ...updated[index], ...patch };
      return updated;
    });
  }, []);

  /**
   * Second, non-streaming call fired after an answer completes (see
   * design/kevin-bot-v2.pen "Panel states"). Failure is silent by design:
   * the suggestion row simply does not render.
   */
  const fetchFollowUps = useCallback(
    async (question: string, answer: string) => {
      const generation = generationRef.current;
      patchLastAssistant({ followUpsLoading: true });
      try {
        const response = await fetch("/api/followups", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ question, answer }),
        });
        if (!response.ok) throw new Error(`Follow-ups request failed: ${response.status}`);
        const data = await response.json();
        const questions: string[] = Array.isArray(data?.questions)
          ? data.questions
              .filter((q: unknown) => typeof q === "string" && q.trim())
              .slice(0, 3)
          : [];
        if (generationRef.current !== generation) return;
        patchLastAssistant({ followUpsLoading: false, followUps: questions });
      } catch {
        if (generationRef.current !== generation) return;
        patchLastAssistant({ followUpsLoading: false, followUps: [] });
      }
    },
    [patchLastAssistant]
  );

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || busyRef.current) return;

      busyRef.current = true;
      lastQuestionRef.current = trimmed;
      setHasError(false);

      const userMsg: Message = { role: "user", content: trimmed };
      setMessages((prev) => [...prev, userMsg]);
      setIsTyping(true);

      ReactGA.event({
        category: "AI Agent",
        action: "Send Message",
        label: "User Question",
      });

      const controller = new AbortController();
      abortRef.current = controller;
      const startedAt = performance.now();

      try {
        const response = await fetch("/api/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ chatInput: trimmed, sessionId }),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const contentType = response.headers.get("content-type") || "";

        if (contentType.includes("text/event-stream")) {
          // Handle streaming response
          setIsTyping(false);
          setIsStreaming(true);
          setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

          const reader = response.body?.getReader();
          if (!reader) throw new Error("No readable stream");

          const decoder = new TextDecoder();
          let accumulated = "";

          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split("\n");

            for (const line of lines) {
              if (line.startsWith("data: ")) {
                const data = line.slice(6).trim();
                if (data === "[DONE]") break;

                try {
                  const parsed = JSON.parse(data);
                  if (parsed.token) {
                    accumulated += parsed.token;
                    const currentText = accumulated;
                    setMessages((prev) => {
                      const updated = [...prev];
                      updated[updated.length - 1] = {
                        role: "assistant",
                        content: currentText,
                      };
                      return updated;
                    });
                  }
                } catch {
                  // Skip malformed SSE lines
                }
              }
            }
          }

          patchLastAssistant({ elapsedMs: Math.round(performance.now() - startedAt) }, true);

          ReactGA.event({
            category: "AI Agent",
            action: "Receive Response",
            label: "Bot Answer (Streamed)",
          });

          if (accumulated.trim()) void fetchFollowUps(trimmed, accumulated);
        } else {
          // Fallback: non-streaming JSON response
          const data = await response.json();
          const elapsedMs = Math.round(performance.now() - startedAt);
          const botResponse: string =
            data.output ||
            (i18n.language.startsWith("es")
              ? "Lo siento, no pude procesar tu solicitud."
              : "I'm sorry, I couldn't process your request.");

          setMessages((prev) => [
            ...prev,
            { role: "assistant", content: botResponse, elapsedMs },
          ]);

          ReactGA.event({
            category: "AI Agent",
            action: "Receive Response",
            label: "Bot Answer",
          });

          // The fallback apology is not a real answer; don't suggest follow-ups for it.
          if (data.output) void fetchFollowUps(trimmed, botResponse);
        }
      } catch (error: any) {
        if (controller.signal.aborted) {
          // Stopped by the user: keep the partial answer, no error, no follow-ups.
          patchLastAssistant({ elapsedMs: Math.round(performance.now() - startedAt) }, true);
        } else {
          console.error("Error chatting with bot:", error);
          // Roll the exchange back: the question returns to the composer and the
          // panel shows the retry block instead of a fake bot apology.
          setMessages((prev) => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last?.role === "assistant" && !last.content) updated.pop();
            if (updated[updated.length - 1]?.role === "user") updated.pop();
            return updated;
          });
          setInput(trimmed);
          setHasError(true);

          ReactGA.event({
            category: "AI Agent",
            action: "Chat Error",
            label: error?.message,
          });
        }
      } finally {
        busyRef.current = false;
        abortRef.current = null;
        setIsTyping(false);
        setIsStreaming(false);
      }
    },
    [i18n.language, sessionId, fetchFollowUps, patchLastAssistant]
  );

  const stopGenerating = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const regenerate = useCallback(async () => {
    if (busyRef.current) return;
    let question: string | null = null;
    setMessages((prev) => {
      const index = prev.map((m) => m.role).lastIndexOf("user");
      if (index < 0) return prev;
      question = prev[index].content;
      return prev.slice(0, index);
    });
    if (question) await sendMessage(question);
  }, [sendMessage]);

  const retryLast = useCallback(async () => {
    const question = lastQuestionRef.current;
    if (!question || busyRef.current) return;
    setHasError(false);
    setInput("");
    await sendMessage(question);
  }, [sendMessage]);

  const clearChat = useCallback(() => {
    play("system");
    abortRef.current?.abort();
    generationRef.current += 1;
    busyRef.current = false;
    lastQuestionRef.current = null;
    setIsTyping(false);
    setIsStreaming(false);
    setHasError(false);
    setInput("");
    setMessages([{ role: "assistant", content: t("kevinBot.panel.welcome") }]);
  }, [play, t]);

  const openChat = useCallback(
    (prefill?: string) => {
      play("system");
      setIsOpen(true);
      ReactGA.event({
        category: "AI Agent",
        action: "Open Chat",
        label: "Kevin-bot",
      });
      if (prefill && prefill.trim()) {
        // Defer so the panel mount animation and the message render nicely.
        setTimeout(() => sendMessage(prefill), 150);
      }
    },
    [play, sendMessage]
  );

  const closeChat = useCallback(() => {
    play("system");
    setIsOpen(false);
  }, [play]);

  const value: ChatContextValue = {
    messages,
    isOpen,
    isTyping,
    isStreaming,
    hasError,
    input,
    setInput,
    openChat,
    closeChat,
    sendMessage,
    stopGenerating,
    regenerate,
    retryLast,
    clearChat,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = (): ChatContextValue => {
  const ctx = useContext(ChatContext);
  if (!ctx) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return ctx;
};
